/**
 * Hooks for case settings management
 */

import { useState, useEffect, useCallback } from 'react'
import type {
  CaseSettings,
  EffectiveCaseSettings,
  OrgCaseDefaults,
  UpdateCaseSettingsRequest,
  UpdateOrgCaseDefaultsRequest,
} from '../types/case-container'
import {
  getCaseSettings,
  updateCaseSettings,
  getOrgCaseDefaults,
  updateOrgCaseDefaults,
} from '../services/settings'

// =============================================================================
// useCaseSettings - Settings for a case
// =============================================================================

interface UseCaseSettingsResult {
  settings: EffectiveCaseSettings | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  update: (updates: UpdateCaseSettingsRequest) => Promise<CaseSettings | null>
  updating: boolean
}

/**
 * Hook to manage case settings
 */
export function useCaseSettings(caseId: string | null): UseCaseSettingsResult {
  const [settings, setSettings] = useState<EffectiveCaseSettings | null>(null)
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setSettings(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getCaseSettings(caseId)
      setSettings(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch case settings')
      setSettings(null)
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  const update = useCallback(
    async (updates: UpdateCaseSettingsRequest): Promise<CaseSettings | null> => {
      if (!caseId) return null

      setUpdating(true)
      setError(null)

      try {
        const data = await updateCaseSettings(caseId, updates)
        // After updating, refetch effective settings to get merged values
        await fetch()
        return data
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update case settings')
        return null
      } finally {
        setUpdating(false)
      }
    },
    [caseId, fetch]
  )

  return { settings, loading, error, refetch: fetch, update, updating }
}

// =============================================================================
// useOrgCaseDefaults - Organisation case defaults
// =============================================================================

interface UseOrgCaseDefaultsResult {
  defaults: OrgCaseDefaults | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  update: (updates: UpdateOrgCaseDefaultsRequest) => Promise<OrgCaseDefaults | null>
  updating: boolean
}

/**
 * Hook to manage organisation case defaults
 */
export function useOrgCaseDefaults(): UseOrgCaseDefaultsResult {
  const [defaults, setDefaults] = useState<OrgCaseDefaults | null>(null)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const data = await getOrgCaseDefaults()
      setDefaults(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch organisation defaults')
      setDefaults(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetch()
  }, [fetch])

  const update = useCallback(
    async (updates: UpdateOrgCaseDefaultsRequest): Promise<OrgCaseDefaults | null> => {
      setUpdating(true)
      setError(null)

      try {
        const data = await updateOrgCaseDefaults(updates)
        setDefaults(data)
        return data
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update organisation defaults')
        return null
      } finally {
        setUpdating(false)
      }
    },
    []
  )

  return { defaults, loading, error, refetch: fetch, update, updating }
}
